import {WidgetType} from '@codemirror/view'

import styles from './upload-placeholder.module.css'

export type UploadPlaceholderWidgetData = {
  id: string
  fileName: string
  mimeType: string
  progressPercent?: number
  previewUrl?: string
  mediaKind?: 'image' | 'video'
}

export class UploadPlaceholderWidget extends WidgetType {
  readonly data: UploadPlaceholderWidgetData

  constructor(data: UploadPlaceholderWidgetData) {
    super()
    this.data = data
  }

  override eq(other: UploadPlaceholderWidget) {
    const a = this.data
    const b = other.data
    return (
      a.fileName === b.fileName &&
      a.mediaKind === b.mediaKind &&
      a.progressPercent === b.progressPercent &&
      a.previewUrl === b.previewUrl
    )
  }

  override toDOM(): HTMLElement {
    const container = document.createElement('div')
    container.className = styles.container
    container.setAttribute('role', 'status')
    container.setAttribute('aria-live', 'polite')
    container.setAttribute('aria-label', `Uploading ${this.data.fileName}`)

    const preview = document.createElement('div')
    preview.className = styles.preview
    container.append(preview)

    // If we have a preview URL (for images/videos), show the preview
    if (this.data.previewUrl && this.data.mediaKind) {
      if (this.data.mediaKind === 'video') {
        const previewMedia = document.createElement('video')
        previewMedia.src = this.data.previewUrl
        previewMedia.className = styles.previewVideo
        previewMedia.muted = true
        previewMedia.playsInline = true
        previewMedia.preload = 'metadata'
        previewMedia.setAttribute('aria-hidden', 'true')
        previewMedia.style.maxWidth = '96px'
        previewMedia.style.maxHeight = '96px'
        previewMedia.style.height = 'auto'
        preview.append(previewMedia)
      } else {
        const previewMedia = document.createElement('img')
        previewMedia.src = this.data.previewUrl
        previewMedia.alt = this.data.fileName
        previewMedia.className = styles.previewImage
        // Apply the same CSS constraints that are applied to the final image
        previewMedia.style.maxWidth = '96px'
        previewMedia.style.maxHeight = '96px'
        previewMedia.style.height = 'auto'
        preview.append(previewMedia)
      }
    } else {
      // Fallback: show file type label
      const fallback = document.createElement('span')
      fallback.className = styles.previewFallback
      fallback.textContent = fallbackLabel(this.data.fileName, this.data.mimeType)
      preview.append(fallback)
    }

    const body = document.createElement('div')
    body.className = styles.body
    container.append(body)

    const fileName = document.createElement('div')
    fileName.className = styles.fileName
    fileName.textContent = this.data.fileName
    body.append(fileName)

    const progress = document.createElement('progress')
    progress.className = styles.progress
    progress.max = 100
    progress.setAttribute('aria-label', 'Upload progress')
    if (typeof this.data.progressPercent === 'number') {
      progress.value = this.data.progressPercent
      progress.textContent = `${this.data.progressPercent}%`
    } else {
      progress.removeAttribute('value')
      progress.textContent = ''
    }
    body.append(progress)

    return container
  }

  override ignoreEvent() {
    return false
  }
}

function fallbackLabel(fileName: string, mimeType: string) {
  if (mimeType) {
    const [, subtype] = mimeType.split('/')
    if (subtype) return subtype.toUpperCase()
  }
  const parts = fileName.split('.')
  return (parts.length > 1 ? parts.at(-1) : undefined)?.toUpperCase() ?? 'FILE'
}
